import {
  GET_SHARED_PREFERENCE,
  LOCATION_EVENT,
  SET_SHARED_PREFERENCE,
} from 'core-lib/constants/nativeBridge';
import {
  LocationRequestResponse,
  UserGeoAddressType,
  UserLocationType,
} from 'core-lib/types/Location';
import { NativePermission } from 'core-lib/types/NativePermission';
import { passMessageToNative, waitForCustomEvent } from './nativeBridge';
import { Address } from 'core-lib/types/Address';
import { LAST_SAVED_LOCATION } from 'core-lib/constants/location';
import { isParkPlusWebView, isPndWebView } from './source';

export const getAddressLine = (data: any) => {
  return [
    data.street ?? '',
    data.subLocality ?? '',
    data.locality ?? '',
    data.subAdministrativeArea ?? '',
    data.administrativeArea ?? '',
    data.country ?? '',
    data.postalCode ?? '',
  ]
    .filter(item => !!item)
    .join(', ');
};

export const isLocationV2Available = () => isParkPlusWebView() || isPndWebView();

export async function checkGPS(): Promise<boolean> {
  const script = `
  try{
    const res = {{result}};
    window.dispatchEvent(new CustomEvent('nativeCheckGpsPermission', {  detail: {res} }));
    } catch(e){
      console.error('callback_script error',e);
    }`;

  passMessageToNative(LOCATION_EVENT, null, {
    action: 'gps',
    type: 'check',
    callback_script: script,
  });

  const eventResult: any = await waitForCustomEvent('nativeCheckGpsPermission');

  return eventResult?.detail?.res ?? false;
}

export async function requestGPS(): Promise<boolean> {
  const script = `
    try{
      const res = {{result}};
      window.dispatchEvent(new CustomEvent('nativeRequestGpsPermission', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    action: 'gps',
    type: 'request',
    callback_script: script,
  });
  const eventResult: any = await waitForCustomEvent('nativeRequestGpsPermission');

  return eventResult?.detail?.res ?? false;
}

export async function checkLocationPermission(): Promise<NativePermission> {
  const script = `
    try{
      const res = '{{result}}';
      window.dispatchEvent(new CustomEvent('nativeCheckLocationPermission', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    action: 'permission',
    type: 'check',
    callback_script: script,
  });

  const eventResult: any = await waitForCustomEvent('nativeCheckLocationPermission');

  return eventResult?.detail?.res ?? NativePermission.Denied;
}

export async function requestLocationPermission(): Promise<NativePermission> {
  const script = `
    try{
      const res = '{{result}}';
      window.dispatchEvent(new CustomEvent('nativeRequestLocationPermission', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    callback_script: script,
    action: 'permission',
    type: 'request',
  });
  const eventResult: any = await waitForCustomEvent('nativeRequestLocationPermission');

  return eventResult?.detail?.res ?? NativePermission.Denied;
}

export async function getLocation(
  locationType: UserLocationType,
): Promise<LocationRequestResponse | null> {
  const script = `
    try{
      const res = JSON.stringify({{result}});
      window.dispatchEvent(new CustomEvent('nativeGetLocation', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    callback_script: script,
    action: 'location',
    type: locationType,
  });

  const eventResult: any = await waitForCustomEvent('nativeGetLocation');

  return eventResult?.detail?.res ? JSON.parse(eventResult.detail.res) : null;
}

export async function getReverseGeoCodeData({
  locationType,
  latLng = null,
}: {
  locationType: UserGeoAddressType;
  latLng?: LocationRequestResponse | any;
}): Promise<{
  response: Address;
  error: string;
}> {
  if (locationType === UserGeoAddressType.LatLng && !latLng) {
    return {
      response: null,
      error: 'No latlon provided',
    };
  }
  const script = `
    try{
      const res = JSON.stringify({{result}});
      window.dispatchEvent(new CustomEvent('nativeGetReverseGeoCodeData', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  const payload: any = {
    callback_script: script,
    action: 'geo',
    type: locationType,
  };

  if (locationType === UserGeoAddressType.LatLng && latLng) {
    payload.lat = latLng.lat;
    payload.lon = latLng.lon;
  }
  passMessageToNative(LOCATION_EVENT, null, payload);

  const eventResult: any = await waitForCustomEvent('nativeGetReverseGeoCodeData');

  if (eventResult?.detail?.res) {
    const response = JSON.parse(eventResult.detail.res);

    if (response) {
      const address: Address = {
        ...response,
        locality: getAddressLine(response),
        pincode: response.postalCode,
        state: response.administrativeArea,
        city: response.subAdministrativeArea,
      };

      if (locationType === UserGeoAddressType.LatLng && latLng) {
        address.latitude = latLng?.lat;
        address.longitude = latLng?.lon ?? latLng?.lng;
      } else {
        address.latitude = response.lat;
        address.longitude = response.lon ?? response?.lng;
      }

      return {
        response: address,
        error: null,
      };
    } else {
      return {
        response: null,
        error: null,
      };
    }
  }
}

export async function openGpsSettings(): Promise<boolean> {
  const script = `
    try{
      const res = {{result}};
      window.dispatchEvent(new CustomEvent('nativeOpenGpsSettings', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    action: 'settings',
    type: 'gps',
    callback_script: script,
  });

  const eventResult: any = await waitForCustomEvent('nativeOpenGpsSettings');

  return eventResult?.detail?.res ?? false;
}

export async function openLocationSettings(): Promise<NativePermission | null> {
  const script = `
    try{
      const res = '{{result}}';
      window.dispatchEvent(new CustomEvent('nativeOpenLocationSettings', {  detail: {res} }));
      } catch(e){
        console.error('callback_script error',e);
      }`;

  passMessageToNative(LOCATION_EVENT, null, {
    action: 'settings',
    type: 'location',
    callback_script: script,
  });

  const eventResult: any = await waitForCustomEvent('nativeOpenLocationSettings');
  return eventResult?.detail?.res ?? null;
}

export const lastLocationFetch = async (): Promise<LocationRequestResponse | null> => {
  passMessageToNative(GET_SHARED_PREFERENCE, null, {
    key: LAST_SAVED_LOCATION,
    callbackScript: `
        window.dispatchEvent(new CustomEvent('user_current_lat_lng', { detail: {res:JSON.stringify(__value__)} }));
      `,
  });
  const resp: any = await waitForCustomEvent('user_current_lat_lng');
  const locRes = resp?.detail?.res ?? null;

  if (!locRes) return null;
  return JSON.parse(locRes);
};

export const saveLastLoc = (data: LocationRequestResponse) => {
  const saveObj: LocationRequestResponse = {
    ...data,
    time: Date.now().toString(),
  };
  passMessageToNative(SET_SHARED_PREFERENCE, null, {
    key: LAST_SAVED_LOCATION,
    value: JSON.stringify(saveObj),
  });
};
